/* ==========================================================================
   Intake — runtime accessibility patches (assets/ada-fixes.js)

   Round 1 (2026-09-15) shipped as an inline script in layout/theme.liquid. Round 2 (2026-09-21, axe-core rescan)
   moved it here unchanged and added the R2 items below. Everything is idempotent, runs once the DOM is ready and
   re-runs (debounced) when sections inject markup later (carousel thumbnails, drawers, app embeds).

   Patches touch attributes and, in three places, tag names. They never change copy, order or layout.
   Each item names the axe rule it clears and where the markup comes from.

   R1  .video-modal-close and icon-only modal buttons -> aria-label            (button-name)
   R1  muted autoplaying decorative loops -> aria-hidden                        (video-caption noise)
   R1  image alt that repeats the caption/link text -> alt=""                   (redundant alt)
   R2a header <nav role="navigation"> x2 with no label -> aria-label            (landmark-unique, 154 pages)
   R2b image/video thumbnail buttons in the custom PDP carousels -> aria-label  (button-name, refills v3 / bundle v2 / all variants)
   R2c horizontal card tracks on why-intake + pregnancy -> tabindex/region      (scrollable-region-focusable)
   R2d #sizingSlider label + live value, video progress bar name,
       carousel stage div with aria-label -> role, untitled iframes -> title,
       Shopify video_tag fallback <img> -> alt="", empty pasted tablist         (label, aria-progressbar-name,
                                                                                 aria-prohibited-attr, frame-title,
                                                                                 image-alt, aria-required-children)
   R2e h4 badges under an h2 -> h3; h4 spec cards under the h1 -> h2; shop tile h3 -> h2  (heading-order)
   R2f blog bodies pasted from Google Docs / Word: li role="presentation",
       alt-less images, nested <main>, image-only headings, empty links        (list, image-alt, landmark-*,
                                                                                 empty-heading, link-name)
   ========================================================================== */
(function () {
  'use strict';

  function norm(s) { return (s || '').replace(/\s+/g, ' ').trim().toLowerCase(); }

  function hasName(el) {
    if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || el.getAttribute('title')) return true;
    if (norm(el.textContent)) return true;
    return !!el.querySelector('img[alt]:not([alt=""]), svg[aria-label], svg title, .sr-only, .visually-hidden');
  }

  /* Replace an element's tag, keeping attributes, children and position. */
  function retag(el, tag) {
    var n = document.createElement(tag);
    for (var i = 0; i < el.attributes.length; i++) n.setAttribute(el.attributes[i].name, el.attributes[i].value);
    while (el.firstChild) n.appendChild(el.firstChild);
    el.parentNode.replaceChild(n, el);
    return n;
  }

  function humanize(path) {
    var seg = (path || '').replace(/^https?:\/\/[^\/]+/, '').split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
    return seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }).trim();
  }

  /* ---------------- R1 — unchanged from the 2026-09-15 inline script ---------------- */
  function round1() {
    document.querySelectorAll('.video-modal-close:not([aria-label])').forEach(function (b) { b.setAttribute('aria-label', 'Close video'); });
    document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])').forEach(function (b) {
      if (b.textContent.trim() === '' && !b.querySelector('img[alt]:not([alt=""]), .sr-only, .visually-hidden')) {
        if (b.closest('.video-modal, [id*="videoModal"]')) b.setAttribute('aria-label', 'Close video');
      }
    });
    document.querySelectorAll('video[autoplay][muted]:not([controls]):not([aria-hidden])').forEach(function (v) { v.setAttribute('aria-hidden', 'true'); v.setAttribute('tabindex', '-1'); });

    document.querySelectorAll('img[alt]:not([alt=""])').forEach(function (img) {
      var alt = norm(img.getAttribute('alt'));
      if (!alt) return;
      var link = img.closest('a');
      if (link && norm(link.textContent) === alt) { img.setAttribute('alt', ''); return; }
      var p = img.parentElement;
      for (var k = 0; k < 2 && p && p !== document.body; k++, p = p.parentElement) {
        if (p.querySelectorAll('img').length !== 1) break;
        if (norm(p.textContent) === alt) { img.setAttribute('alt', ''); break; }
      }
    });
  }

  /* ---------------- R2a — landmark-unique ---------------- */
  function navLabels() {
    var mainSeen = false;
    document.querySelectorAll('nav:not([aria-label]):not([aria-labelledby])').forEach(function (nav) {
      if (nav.closest('.shopify-section--header')) {
        if (nav.closest('.header__icon-list')) { nav.setAttribute('aria-label', 'Account and cart'); }
        else if (!mainSeen) { nav.setAttribute('aria-label', 'Main menu'); mainSeen = true; }
        else { nav.setAttribute('aria-label', 'Secondary menu'); }
      } else if (nav.closest('.shopify-section--footer')) {
        nav.setAttribute('aria-label', 'Footer');
      }
    });
  }

  /* ---------------- R2b — button-name (media thumbnails) ---------------- */
  function thumbNames() {
    var buttons = [];
    document.querySelectorAll('button[data-thumb], [id$="CarouselThumbs"] > button').forEach(function (b) { buttons.push(b); });
    document.querySelectorAll('[class*="thumb-inner"]').forEach(function (d) { var b = d.closest('button'); if (b) buttons.push(b); });
    buttons.forEach(function (b) {
      if (b.getAttribute('aria-label') || b.getAttribute('aria-labelledby')) return;
      var sibs = Array.prototype.filter.call(b.parentElement ? b.parentElement.children : [], function (c) { return c.tagName === 'BUTTON'; });
      var idx = sibs.indexOf(b) + 1;
      var img = b.querySelector('img[alt]:not([alt=""])');
      var kind = b.querySelector('video') ? 'video' : 'image';
      b.setAttribute('aria-label', img ? img.getAttribute('alt') : 'View ' + kind + ' ' + idx + (sibs.length ? ' of ' + sibs.length : ''));
    });
  }

  /* ---------------- R2c — scrollable-region-focusable ---------------- */
  function scrollRegions() {
    document.querySelectorAll('.uc-scroll-track, .wim-scroll-track, .mom-review-track, [class*="scroll-track"], [class*="review-track"]').forEach(function (el) {
      if (el.hasAttribute('tabindex') || el.closest('[tabindex]')) return;
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'region');
      el.setAttribute('data-ada-scroll-region', '');
      if (!el.getAttribute('aria-label')) {
        var scope = el.closest('section, [class*="section"]') || el.parentElement;
        var h = scope ? scope.querySelector('h1, h2, h3') : null;
        var text = h && !el.contains(h) ? norm(h.textContent) : '';
        el.setAttribute('aria-label', text ? text.charAt(0).toUpperCase() + text.slice(1, 80) + ' — scrollable cards' : 'Scrollable cards');
      }
    });
  }

  /* ---------------- R2d — assorted names, roles and titles ---------------- */
  function misc() {
    /* label: the size-finder range input on /pages/how-intake-works */
    var slider = document.getElementById('sizingSlider');
    if (slider && !slider.getAttribute('aria-label')) {
      slider.setAttribute('aria-label', 'Choose your band size');
      var name = document.querySelector('.sizing-active-name');
      var sync = function () { if (name && norm(name.textContent)) slider.setAttribute('aria-valuetext', name.textContent.trim()); };
      slider.addEventListener('input', function () { setTimeout(sync, 0); });
      sync();
    }

    /* aria-progressbar-name: video progress bars on /pages/how-it-works, /pages/how-to-apply-v2 */
    document.querySelectorAll('[role="progressbar"]:not([aria-label]):not([aria-labelledby])').forEach(function (p) {
      p.setAttribute('aria-label', p.closest('[class*="video"], [id*="video"]') ? 'Video progress' : 'Progress');
    });

    /* aria-prohibited-attr: a focusable <div aria-label> needs a role for the label to be allowed */
    document.querySelectorAll('.ipdp-carousel-stage[aria-label]:not([role]), .irf-carousel-stage[aria-label]:not([role])').forEach(function (d) { d.setAttribute('role', 'region'); });

    /* frame-title: third-party embeds (Instagram, TikTok, VideoPress) in blog posts and pages */
    document.querySelectorAll('iframe:not([title]), iframe[title=""]').forEach(function (f) {
      var src = f.getAttribute('src') || f.getAttribute('data-src') || f.getAttribute('name') || '';
      var t = /instagram/i.test(src) ? 'Instagram post'
            : /tiktok|__tt_embed/i.test(src) ? 'TikTok video'
            : /youtube|youtu\.be|vimeo|videopress|wistia|loom/i.test(src) ? 'Video'
            : /maps\.google|google\.com\/maps/i.test(src) ? 'Map'
            : 'Embedded content';
      f.setAttribute('title', t);
    });

    /* image-alt: Shopify's video_tag filter emits a fallback <img> with no alt inside every <video> */
    document.querySelectorAll('video > img:not([alt])').forEach(function (i) { i.setAttribute('alt', ''); });

    /* aria-required-children: an empty <div role="tablist"> pasted into rich-text content (collection description) */
    document.querySelectorAll('.prose [role="tablist"], .rte [role="tablist"]').forEach(function (t) {
      if (!t.querySelector('[role="tab"]')) { t.removeAttribute('role'); t.removeAttribute('aria-label'); }
    });
  }

  /* ---------------- R2e — heading-order ---------------- */
  function headings() {
    document.querySelectorAll('.stays-badge h4').forEach(function (h) { retag(h, 'h3'); }); /* follow an h2 */
    document.querySelectorAll('.pdp-spec h4').forEach(function (h) { retag(h, 'h2'); });    /* follow the product h1 */
    document.querySelectorAll('h3.pc-name').forEach(function (h) { retag(h, 'h2'); });      /* follow the page h1 */
  }

  /* ---------------- R2f — blog bodies pasted from Google Docs / Word ---------------- */
  function articleContent() {
    var roots = document.querySelectorAll('.shopify-section--main-article .prose, .shopify-section--main-article .rte, .shopify-section--main-article .article__content');
    roots.forEach(function (root) {
      /* list: <li role="presentation"> removes the item from the list for screen readers */
      root.querySelectorAll('li[role="presentation"], li[role="none"]').forEach(function (li) { li.removeAttribute('role'); });

      /* presentation-role-conflict: an image marked presentational but also given alt text / tabindex */
      root.querySelectorAll('img[role="presentation"], img[role="none"]').forEach(function (img) {
        if (img.hasAttribute('tabindex') || norm(img.getAttribute('alt'))) img.removeAttribute('role');
      });

      /* image-alt: images with no alt attribute at all are read as their (Google-hosted) URL.
         Marked decorative here so nothing is read aloud; real descriptions still have to be written in the article
         editor — see the content punch list. */
      root.querySelectorAll('img:not([alt])').forEach(function (img) { img.setAttribute('alt', ''); });

      /* landmark-main-is-top-level / landmark-no-duplicate-main: a <main> pasted inside the article */
      root.querySelectorAll('main').forEach(function (m) { retag(m, 'div'); });

      /* empty-heading: headings that wrap only an image or nothing at all */
      root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (h) {
        if (!norm(h.textContent) && !h.querySelector('img[alt]:not([alt=""])')) retag(h, h.querySelector('img') ? 'p' : 'div');
      });

      /* link-name: anchors with no accessible name */
      root.querySelectorAll('a[href]').forEach(function (a) {
        if (hasName(a)) return;
        var href = a.getAttribute('href') || '';
        if (a.querySelector('img')) {
          var label = /\/pages\/shop/.test(href) ? 'Shop Intake'
                    : /^(https?:\/\/(www\.)?intakebreathing\.com\/?|\/)$/.test(href) ? 'Intake Breathing home page'
                    : humanize(href) || 'Link';
          a.setAttribute('aria-label', label);
        } else if (!norm(a.textContent)) {
          /* A completely empty anchor left behind by a paste: nothing to click, so hide it from assistive tech. */
          a.setAttribute('aria-hidden', 'true');
          a.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  function patch() {
    [round1, navLabels, thumbNames, scrollRegions, misc, headings, articleContent].forEach(function (fn) {
      try { fn(); } catch (e) { if (window.console && console.warn) console.warn('[ada-fixes] ' + fn.name, e); }
    });
  }

  var scheduled = null;
  function schedule() {
    if (scheduled) return;
    scheduled = setTimeout(function () { scheduled = null; patch(); }, 150);
  }

  function start() {
    patch();
    if (window.MutationObserver && document.body) {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
